/**
 * หน้าโฆษณา (/ads, owner only) — pure logic, unit-tested in test/adsview.spec.ts:
 * tabs + who may open the page · the period presets · the column catalogue of the campaign table (every AdMetrics
 * number) · rows per level, filters, sorting, the "รวมที่กรอง" sum · saved views · the overview KPIs ·
 * recommendation groups · AI tag wording. Spec: docs/superpowers/specs/2026-10-20-ads-command-center-design.md.
 * The AI วิเคราะห์ tab's own logic: lib/adsAi.ts + lib/miniMarkdown.ts · หน้า Landing: lib/adsLp.ts.
 */
import type {
  AdLevel,
  AdMetrics,
  AdNode,
  AdsCompareQuery,
  AdsOverview,
  AdsQuery,
  CreativeAiBudget,
  CreativeTags,
  RecKind,
  Recommendation,
  SavedView,
} from '../api/ads/types'
import type { DashRange } from '../api/dash/types'
import type { StaffRole } from '../api/session'
import type { IconName } from '../components/ui/icons'
import { addDays, count, customProblem, money, pct } from './dash'
import { baht2, compareRows, ctrText, freqText, isSynthetic, leaves, roasText, type SortDir } from './ads'

// ---------------------------------------------------------------- page + who

export type AdsTabId = 'overview' | 'campaigns' | 'creatives' | 'quality' | 'actions' | 'ai' | 'lp'

/** `badge` = a small chip after the label (หน้า Landing "ใหม่", the approved mockup 24 ต.ค. 69) */
export const ADS_TABS: { id: AdsTabId; label: string; icon: IconName; badge?: string }[] = [
  { id: 'overview', label: 'ภาพรวม', icon: 'dashboard' },
  { id: 'campaigns', label: 'แคมเปญ', icon: 'layers' },
  { id: 'creatives', label: 'ครีเอทีฟ', icon: 'image' },
  { id: 'quality', label: 'คุณภาพแชท', icon: 'chat' },
  { id: 'actions', label: 'สิ่งที่ควรทำ', icon: 'target' },
  { id: 'ai', label: 'AI วิเคราะห์', icon: 'sparkles' },
  // A/B of the web pages ads send people to (components/ads/LpTab.vue · lib/adsLp.ts · spec 2026-10-24-lp-ab-design)
  { id: 'lp', label: 'หน้า Landing', icon: 'browser', badge: 'ใหม่' },
]

export function tabOf(v: unknown): AdsTabId {
  return ADS_TABS.some((t) => t.id === v) ? (v as AdsTabId) : 'overview'
}

/** โฆษณา is the owner's page (the server answers 403 to anyone else). */
export const canSeeAds = (role: StaffRole | null | undefined): boolean => role === 'owner'

/** Route names only the owner may open: App.vue sends anyone else back to the chats. */
export const OWNER_ONLY_PAGES = new Set(['ads', 'posts'])

export function pageAllowed(name: string, me: { role: StaffRole; reviewer?: boolean } | null | undefined): boolean {
  if (!me || !OWNER_ONLY_PAGES.has(name)) return true
  return canSeeAds(me.role) && !me.reviewer
}

// ---------------------------------------------------------------- period (address bar: range · offset · from · to · window)
// The date picker itself (presets, ใช้ล่าสุด, compare, the URL's p / cmp params) is lib/adsPeriod.ts.

export interface PeriodQuery {
  range: DashRange
  offset: number
  from: string | null
  to: string | null
}

const RANGES = new Set<DashRange>(['today', '7d', '30d', '90d', 'custom'])
/** the old "last N days" links (?range=7d …) = the last N whole days on this page */
const WHOLE: [DashRange, number][] = [['7d', 7], ['30d', 30], ['90d', 90]]

/**
 * "N วันที่ผ่านมา" on โฆษณา = the last N WHOLE days, up to yesterday (Meta Ads Manager's "Last 7 days" too): Meta's spend
 * and the shop's sales come per day, so a period ending with today's first hours, compared with whole days before,
 * read as a drop every morning (20 ต.ค. 69 03:00: ยอดร้าน "-14%" was flat per day). `back` = periods before (old links).
 */
export function wholeDays(n: number, today: string, back = 0): PeriodQuery {
  return { range: 'custom', offset: 0, from: addDays(today, -n * (back + 1)), to: addDays(today, -1 - n * back) }
}

/** The period from the address bar (no range = วันนี้, owner 20 ต.ค. 69 — AdsView first applies the owner's last choice
 *  when the address has no period at all); a custom range that does not work → the last 14 days. */
export function periodFromQuery(q: Record<string, unknown>, today: string): PeriodQuery {
  const r = String(q.range ?? 'today') as DashRange
  const range: DashRange = RANGES.has(r) ? r : 'today'
  if (range === 'custom') {
    const from = q.from ? String(q.from) : null
    const to = q.to ? String(q.to) : null
    if (customProblem(from, to, today)) return { range, offset: 0, from: addDays(today, -13), to: today }
    return { range, offset: 0, from, to: to! > today ? today : to }
  }
  const offset = Math.max(0, Math.min(52, parseInt(String(q.offset ?? '0'), 10) || 0))
  const whole = WHOLE.find(([v]) => v === range)
  if (whole) return wholeDays(whole[1], today, offset)
  return { range, offset, from: null, to: null }
}

/** 7 | 30 when the owner switched the counting window, else null (their setting). */
export function windowFromQuery(q: Record<string, unknown>): number | null {
  const w = parseInt(String(q.window ?? ''), 10)
  return w === 7 || w === 30 ? w : null
}

/** `compare` (report + overview): null = the previous period (lib/adsPeriod compareQuery). */
export const adsQuery = (p: PeriodQuery, window: number | null = null, compare: AdsCompareQuery | null = null): AdsQuery =>
  compare ? { ...p, window, compare } : { ...p, window }

/** Query params of a period (offset 0 and empty days left out) — for links to /ads and router.replace. `range` is always
 *  there: an address without a period opens the owner's last choice. */
export function periodParams(p: PeriodQuery): Record<string, string> {
  const out: Record<string, string> = { range: p.range }
  if (p.range !== 'custom' && p.offset) out.offset = String(p.offset)
  if (p.range === 'custom' && p.from && p.to) {
    out.from = p.from
    out.to = p.to
  }
  return out
}

// ---------------------------------------------------------------- the campaign table: columns

export type ColGroup = 'money' | 'customers' | 'chats' | 'meta' | 'delivery'

export const COL_GROUPS: { id: ColGroup; label: string }[] = [
  { id: 'money', label: 'เงินและกำไร' },
  { id: 'customers', label: 'ออเดอร์และลูกค้า' },
  { id: 'chats', label: 'แชทและคุณภาพแชท' },
  { id: 'meta', label: 'Meta บอก (ไว้เทียบ)' },
  { id: 'delivery', label: 'การเห็นแอด' },
]

export type Tone = 'crit' | 'warn' | 'good' | 'mute'

export interface Col {
  key: string
  label: string
  /** what the number means (header tooltip + the picker) */
  title: string
  group: ColGroup
  /** column width (px) */
  w: number
  /** first click: high → low ('desc') or, for costs, low → high */
  first: SortDir
  /** the number it sorts by (null = — , always last) */
  val: (m: AdMetrics) => number | null
  text: (m: AdMetrics) => string
  /** a quiet second line under the number */
  sub?: (m: AdMetrics) => string | null
  /** colour of the number */
  tone?: (m: AdMetrics) => Tone | null
  /** a chip under the number (ROAS vs break-even) */
  chip?: (m: AdMetrics) => { text: string; tone: Tone; title: string } | null
}

const nz = (v: number | null | undefined): number | null => (v === null || v === undefined ? null : v)

/** Money that can be a loss: -฿2,499 (lib/dash money would say ฿-2,499) · — */
export const moneySigned = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : n < 0 ? `-${money(-n)}` : money(n))

/** LTV 90 days per new customer (later orders within 90 days of the first; first order left out). */
export function ltvPer(m: Pick<AdMetrics, 'ltv90_revenue' | 'new_orders'>): number | null {
  const r = nz(m.ltv90_revenue)
  const n = nz(m.new_orders)
  return r !== null && n ? r / n : null
}

/** ROAS against the break-even point (1 ÷ margin): ขาดทุน · คุ้มทุน · กำไรดี (≥ 1.5 ×). */
export function roasVsBreakeven(roas: number | null | undefined, be: number | null | undefined): { text: string; tone: Tone; title: string } | null {
  const r = nz(roas)
  const b = nz(be)
  if (r === null || b === null || !(b > 0)) return null
  const title = `จุดคุ้มทุน ROAS ${roasText(b)} (= 1 ÷ มาร์จิ้นของออเดอร์)`
  if (r < b) return { text: 'ขาดทุน', tone: 'crit', title }
  if (r < b * 1.5) return { text: 'คุ้มทุน', tone: 'mute', title }
  return { text: 'กำไรดี', tone: 'good', title }
}

/** Meta's own purchase count credits an ad with anyone who clicked or saw it before buying (20 ต.ค. 69: 409 purchases in
 * a week the shop had 392 orders) — it runs high, so it is shown next to ours, never instead of it. */
export const META_RUNS_HIGH = 'Meta นับรวมคนที่เคยเห็นหรือคลิกแอดก่อนซื้อ จึงมักสูงกว่าที่แอดพามาจริง ใช้เทียบเท่านั้น'
/** ROAS by Meta's count: its purchase value ÷ spend */
export const metaRoas = (m: Pick<AdMetrics, 'meta_purchase_value' | 'spend'>): number | null => (m.spend > 0 ? Math.round((m.meta_purchase_value / m.spend) * 100) / 100 : null)

/** v1: new vs returning, CAC and LTV come from web orders only (api/app/meta_ads, package A) */
export const WEB_ONLY = 'นับจากออเดอร์เว็บ'
/** what makes a chat "คุณภาพ" (api/app/meta_ads/quality: asking needs at least 2 customer messages) */
export const QUALITY_DEF = 'แชทที่ส่งเบอร์หรือที่อยู่ หรือถามราคา ค่าส่ง วันส่ง เมนู พื้นที่ส่ง ลิงก์หรือวิธีสั่ง (ลูกค้าพิมพ์อย่างน้อย 2 ข้อความ)'
/** the columns that come from web orders only */
export const WEB_ONLY_COLS = new Set(['new_ret', 'new_revenue', 'returning_revenue', 'cac', 'ltv90'])

const profitTone = (v: number | null | undefined): Tone | null => (nz(v) !== null && v! < 0 ? 'crit' : null)

export const COLUMNS: Col[] = [
  // money
  { key: 'spend', label: 'ใช้จ่าย', title: 'ค่าโฆษณาที่ Meta คิดในช่วงนี้', group: 'money', w: 92, first: 'desc', val: (m) => m.spend, text: (m) => money(m.spend) },
  {
    key: 'revenue', label: 'ยอดขาย', title: 'ยอดขายที่ร้านนับเอง (แชทและเว็บ) ภายในช่วงนับยอดหลังลูกค้ามาจากแอด', group: 'money', w: 96, first: 'desc',
    val: (m) => m.revenue, text: (m) => money(m.revenue),
  },
  {
    key: 'profit', label: 'กำไร', title: 'ยอดขาย − ต้นทุนจริงจาก POS − ค่าแอด · ออเดอร์ที่ไม่มีต้นทุนใช้มาร์จิ้นสำรอง', group: 'money', w: 96, first: 'desc',
    val: (m) => nz(m.profit), text: (m) => moneySigned(m.profit), tone: (m) => profitTone(m.profit),
  },
  {
    key: 'roas', label: 'ROAS', title: 'ยอดขาย ÷ ค่าแอด เทียบจุดคุ้มทุน (1 ÷ มาร์จิ้น)', group: 'money', w: 84, first: 'desc',
    val: (m) => m.roas, text: (m) => roasText(m.roas), chip: (m) => roasVsBreakeven(m.roas, m.breakeven_roas),
  },
  {
    key: 'gross_profit', label: 'กำไรขั้นต้น', title: 'ยอดขาย − ต้นทุนจริง ก่อนหักค่าแอด', group: 'money', w: 96, first: 'desc',
    val: (m) => nz(m.gross_profit), text: (m) => moneySigned(m.gross_profit), tone: (m) => profitTone(m.gross_profit),
  },
  { key: 'margin_pct', label: 'มาร์จิ้น', title: 'กำไรขั้นต้น ÷ ยอดขาย', group: 'money', w: 72, first: 'desc', val: (m) => nz(m.margin_pct), text: (m) => pct(m.margin_pct) },
  {
    key: 'breakeven_roas', label: 'จุดคุ้มทุน', title: 'ROAS ที่แอดจ่ายค่าตัวเองพอดี = 1 ÷ มาร์จิ้น', group: 'money', w: 80, first: 'asc',
    val: (m) => nz(m.breakeven_roas), text: (m) => roasText(m.breakeven_roas),
  },
  { key: 'aov', label: 'ออเดอร์เฉลี่ย', title: 'ยอดขาย ÷ ออเดอร์', group: 'money', w: 92, first: 'desc', val: (m) => m.aov, text: (m) => money(m.aov) },
  // customers
  { key: 'orders', label: 'ออเดอร์', title: 'ออเดอร์จากแชทและจากเว็บ', group: 'customers', w: 68, first: 'desc', val: (m) => m.orders, text: (m) => count(m.orders) },
  {
    key: 'cost_per_order', label: 'ต้นทุน/ออเดอร์', title: 'ค่าแอด ÷ ออเดอร์', group: 'customers', w: 92, first: 'asc',
    val: (m) => m.cost_per_order, text: (m) => money(m.cost_per_order),
  },
  {
    key: 'new_ret', label: 'ใหม่/เก่า', title: `ออเดอร์จากลูกค้าใหม่ (เบอร์ที่ไม่เคยสั่ง) / ลูกค้าเก่า · ${WEB_ONLY}`, group: 'customers', w: 76, first: 'desc',
    val: (m) => nz(m.new_orders),
    text: (m) => (nz(m.new_orders) === null ? '—' : `${count(m.new_orders)} / ${count(m.returning_orders)}`),
  },
  {
    key: 'new_revenue', label: 'ยอดลูกค้าใหม่', title: `ยอดขายจากลูกค้าใหม่ · ${WEB_ONLY}`, group: 'customers', w: 96, first: 'desc',
    val: (m) => nz(m.new_revenue), text: (m) => money(m.new_revenue),
  },
  {
    key: 'returning_revenue', label: 'ยอดลูกค้าเก่า', title: `ยอดขายจากลูกค้าที่เคยสั่งแล้ว · ${WEB_ONLY}`, group: 'customers', w: 96, first: 'desc',
    val: (m) => nz(m.returning_revenue), text: (m) => money(m.returning_revenue),
  },
  { key: 'cac', label: 'CAC', title: `ค่าแอด ÷ ลูกค้าใหม่ที่แอดพามา · ${WEB_ONLY}`, group: 'customers', w: 76, first: 'asc', val: (m) => nz(m.cac), text: (m) => money(m.cac) },
  {
    key: 'ltv90', label: 'LTV 90 วัน', title: `ลูกค้าใหม่จากแอดนี้ซื้อซ้ำอีกเท่าไรใน 90 วันหลังออเดอร์แรก เฉลี่ยต่อคน (ไม่รวมออเดอร์แรก · ไม่รวม Shopee/TikTok) · ${WEB_ONLY}`,
    group: 'customers', w: 96, first: 'desc', val: (m) => ltvPer(m), text: (m) => money(ltvPer(m)),
    sub: (m) => (ltvPer(m) !== null && !m.ltv90_complete ? 'ยังนับไม่ครบ' : null),
  },
  // chats
  { key: 'chats', label: 'แชท', title: 'แชทในกล่องข้อความของเราที่มาจากแอดนี้', group: 'chats', w: 60, first: 'desc', val: (m) => m.chats, text: (m) => count(m.chats) },
  { key: 'cost_per_chat', label: 'ต้นทุน/แชท', title: 'ค่าแอด ÷ แชท', group: 'chats', w: 84, first: 'asc', val: (m) => m.cost_per_chat, text: (m) => money(m.cost_per_chat) },
  {
    key: 'chat_to_order', label: 'แชท→ออเดอร์', title: 'แชทที่สั่งซื้อภายในช่วงนับยอด ÷ แชททั้งหมด', group: 'chats', w: 88, first: 'desc',
    val: (m) => m.chat_to_order, text: (m) => pct(m.chat_to_order),
  },
  {
    key: 'quality_rate', label: 'คุณภาพแชท', title: `${QUALITY_DEF} ÷ แชททั้งหมด`, group: 'chats', w: 88, first: 'desc',
    val: (m) => nz(m.quality_rate), text: (m) => pct(m.quality_rate),
    sub: (m) => (nz(m.quality_rate) === null ? null : `${count(m.chats_quality)} แชท`),
  },
  { key: 'chats_quality', label: 'แชทคุณภาพ', title: QUALITY_DEF, group: 'chats', w: 80, first: 'desc', val: (m) => m.chats_quality ?? null, text: (m) => count(m.chats_quality) },
  { key: 'buyers', label: 'คนซื้อ', title: 'คนจากแชทเหล่านี้ที่ซื้อภายใน 7 วัน', group: 'chats', w: 64, first: 'desc', val: (m) => m.buyers ?? null, text: (m) => count(m.buyers) },
  {
    key: 'cost_per_quality_chat', label: 'ต้นทุน/แชทคุณภาพ', title: 'ค่าแอด ÷ แชทคุณภาพ', group: 'chats', w: 104, first: 'asc',
    val: (m) => nz(m.cost_per_quality_chat), text: (m) => money(m.cost_per_quality_chat),
  },
  {
    key: 'cost_per_buyer', label: 'ต้นทุน/คนซื้อ', title: 'ค่าแอด ÷ คนที่ซื้อจริง', group: 'chats', w: 92, first: 'asc',
    val: (m) => nz(m.cost_per_buyer), text: (m) => money(m.cost_per_buyer),
  },
  // Meta's own numbers
  {
    key: 'meta', label: 'Meta บอก', title: `ยอดซื้อที่ Meta นับให้แอดเอง · ${META_RUNS_HIGH}`, group: 'meta', w: 92,
    first: 'desc', val: (m) => m.meta_purchase_value, text: (m) => money(m.meta_purchase_value), sub: (m) => `ซื้อ ${count(m.meta_purchases)} ครั้ง`,
  },
  {
    key: 'meta_roas', label: 'ROAS (Meta)', title: `ยอดซื้อที่ Meta นับ ÷ ค่าแอด เทียบจุดคุ้มทุน · ${META_RUNS_HIGH}`, group: 'meta', w: 92, first: 'desc',
    val: (m) => metaRoas(m), text: (m) => roasText(metaRoas(m)), chip: (m) => roasVsBreakeven(metaRoas(m), m.breakeven_roas),
  },
  { key: 'meta_chats', label: 'Meta: เริ่มแชท', title: 'การสนทนาที่ Meta นับว่าเริ่มจากแอด (วิธีนับของ Meta)', group: 'meta', w: 92, first: 'desc', val: (m) => m.meta_chats, text: (m) => count(m.meta_chats) },
  // delivery
  { key: 'impressions', label: 'เห็นแอด', title: 'จำนวนครั้งที่แอดแสดง', group: 'delivery', w: 84, first: 'desc', val: (m) => m.impressions, text: (m) => count(m.impressions) },
  { key: 'link_clicks', label: 'คลิกลิงก์', title: 'คลิกลิงก์ในแอด', group: 'delivery', w: 76, first: 'desc', val: (m) => m.link_clicks, text: (m) => count(m.link_clicks) },
  { key: 'ctr', label: 'CTR', title: 'คลิกลิงก์ ÷ ครั้งที่เห็น', group: 'delivery', w: 64, first: 'desc', val: (m) => m.ctr, text: (m) => ctrText(m.ctr) },
  { key: 'cpc', label: 'CPC', title: 'ค่าแอด ÷ คลิกลิงก์', group: 'delivery', w: 72, first: 'asc', val: (m) => m.cpc, text: (m) => baht2(m.cpc) },
  { key: 'cpm', label: 'CPM', title: 'ค่าแอดต่อการเห็น 1,000 ครั้ง', group: 'delivery', w: 76, first: 'asc', val: (m) => m.cpm, text: (m) => baht2(m.cpm) },
  { key: 'frequency', label: 'ความถี่', title: 'คนหนึ่งเห็นแอดเฉลี่ยกี่ครั้ง', group: 'delivery', w: 64, first: 'desc', val: (m) => m.frequency, text: (m) => freqText(m.frequency) },
  { key: 'hook_rate', label: 'ดูเกิน 3 วิ', title: 'วิดีโอ: ดูเกิน 3 วินาที ÷ ครั้งที่เห็น', group: 'delivery', w: 76, first: 'desc', val: (m) => m.hook_rate, text: (m) => pct(m.hook_rate) },
  { key: 'hold_rate', label: 'ดูจนจบ', title: 'วิดีโอ: ดูจนจบ ÷ คนที่ดูเกิน 3 วินาที', group: 'delivery', w: 68, first: 'desc', val: (m) => m.hold_rate, text: (m) => pct(m.hold_rate) },
]

const COL = new Map(COLUMNS.map((c) => [c.key, c]))
export const colOf = (key: string): Col | null => COL.get(key) ?? null

export const DEFAULT_COLUMNS = ['spend', 'revenue', 'profit', 'roas', 'orders', 'new_ret', 'cac', 'chats', 'meta', 'meta_roas']

/** Known keys only, each once, in order; nothing left → the default set. */
export function cleanColumns(keys: readonly string[] | null | undefined): string[] {
  const out = [...new Set((keys ?? []).filter((k) => COL.has(k)))]
  return out.length ? out : [...DEFAULT_COLUMNS]
}

/** Grid columns: the name (220 px at least — 150 on a phone, --nw in adsview.css), then each number column at its width. */
export const gridOf = (cols: readonly Col[]): string => ['minmax(var(--nw, 220px), 1fr)', ...cols.map((c) => `${c.w}px`)].join(' ')
/** The table's least width (name 220 + columns + gaps 12 + padding 2 × 20): wider → it scrolls inside its card. */
export const minWidthOf = (cols: readonly Col[]): number => 220 + cols.reduce((t, c) => t + c.w + 12, 0) + 40

// ---------------------------------------------------------------- the campaign table: rows, filters, sorting, sums

export interface TableFilters {
  status?: string
  flag?: string
  q?: string
}

export const STATUS_OPTIONS = [
  { v: '', label: 'ทุกสถานะ' },
  { v: 'active', label: 'กำลังแสดง' },
  { v: 'paused', label: 'หยุดอยู่' },
] as const

export const FLAG_OPTIONS = [
  { v: '', label: 'ทุกแถว' },
  { v: 'any', label: 'มีเรื่องต้องดู' },
  { v: 'loss', label: 'ขาดทุนหลังหักค่าแอด' },
  { v: 'no_sales', label: 'ขายไม่ได้' },
  { v: 'cpo_high', label: 'ต้นทุนสูง' },
  { v: 'fatigue', label: 'เริ่มล้า' },
] as const

export const LEVEL_OPTIONS: { v: AdLevel; label: string }[] = [
  { v: 'campaign', label: 'แคมเปญ' },
  { v: 'adset', label: 'ชุดโฆษณา' },
  { v: 'ad', label: 'แอด' },
]

export const hasFilter = (f: TableFilters): boolean => !!(f.status || f.flag || f.q?.trim())

/** The rows at a level: campaigns (› ad sets › ads) · ad sets (› ads) · ads only. */
export function treeAt(tree: AdNode[], level: AdLevel): AdNode[] {
  if (level === 'campaign') return tree
  if (level === 'adset') return tree.flatMap((c) => c.children ?? [])
  return leaves(tree)
}

/** status / flag are judged on the bottom rows (ads); their parents stay when any child stays. */
function leafOk(n: AdNode, f: TableFilters): boolean {
  if (f.status === 'active' && !n.active) return false
  if (f.status === 'paused' && (n.active || isSynthetic(n))) return false
  if (f.flag) {
    if (f.flag === 'any') return n.flags.some((x) => x === 'no_sales' || x === 'cpo_high' || x === 'fatigue') || (nz(n.profit) !== null && n.profit! < 0)
    if (f.flag === 'loss') return nz(n.profit) !== null && n.profit! < 0 && !isSynthetic(n)
    return n.flags.includes(f.flag as never)
  }
  return true
}

const norm = (t: string): string => t.toLowerCase().replace(/\s+/g, ' ').trim()

/** A name that matches the search keeps everything under it (still through status / flag). */
export function filterTree(nodes: AdNode[], f: TableFilters, under = false): AdNode[] {
  const q = norm(f.q ?? '')
  const out: AdNode[] = []
  for (const n of nodes) {
    const hit = under || !q || norm(n.name).includes(q) || n.id === q
    if (n.children?.length) {
      const kids = filterTree(n.children, f, hit)
      if (kids.length) out.push({ ...n, children: kids })
    } else if (hit && leafOk(n, f)) out.push(n)
  }
  return out
}

/** Sort every level inside its parent by a column (— last either way; ties → more spend → name). New arrays. */
export function sortBy(nodes: AdNode[], col: Col, dir: SortDir): AdNode[] {
  return [...nodes].sort(compareRows<AdNode>(col.val, dir)).map((n) => (n.children?.length ? { ...n, children: sortBy(n.children, col, dir) } : n))
}

export function emptyMetrics(): AdMetrics {
  return {
    spend: 0, impressions: 0, clicks: 0, link_clicks: 0, ctr: null, cpc: null, cpm: null, frequency: null, meta_chats: 0, chats: 0, cost_per_chat: null,
    orders_chat: 0, orders_web: 0, orders: 0, revenue_chat: 0, revenue_web: 0, revenue: 0, roas: null, cost_per_order: null, chat_to_order: null, aov: null,
    new_customers: null, returning_customers: null, meta_purchases: 0, meta_purchase_value: 0, video_3s: 0, thruplays: 0, hook_rate: null, hold_rate: null,
    profit: null, gross_profit: null, margin_pct: null, breakeven_roas: null, new_orders: null, returning_orders: null, new_revenue: null,
    returning_revenue: null, cac: null, ltv90_revenue: null, ltv90_complete: false, chats_quality: 0, chats_contact: 0, chats_intent: 0, buyers: 0,
    quality_rate: null, cost_per_quality_chat: null, cost_per_buyer: null,
  }
}

const r2 = (n: number): number => Math.round(n * 100) / 100
const r1 = (n: number): number => Math.round(n * 10) / 10

/**
 * The numbers of several rows together ("รวมที่กรอง"): counts and money add up, rates are worked out again from the
 * sums (never averaged) · a part that is unknown (null) on every row stays null · frequency cannot be added → null.
 */
export function sumMetrics(rows: readonly AdMetrics[]): AdMetrics {
  const s = emptyMetrics()
  const add = (k: keyof AdMetrics) => rows.reduce((t, r) => t + ((r[k] as number | null | undefined) ?? 0), 0)
  const addN = (k: keyof AdMetrics) => (rows.some((r) => nz(r[k] as number | null) !== null) ? add(k) : null)
  for (const k of ['impressions', 'clicks', 'link_clicks', 'meta_chats', 'chats', 'orders_chat', 'orders_web', 'orders', 'revenue_chat', 'revenue_web',
    'revenue', 'meta_purchases', 'meta_purchase_value', 'video_3s', 'thruplays', 'chats_quality', 'chats_contact', 'chats_intent', 'buyers'] as const) {
    ;(s[k] as number) = add(k)
  }
  s.spend = r2(add('spend'))
  for (const k of ['new_customers', 'returning_customers', 'new_orders', 'returning_orders', 'new_revenue', 'returning_revenue', 'gross_profit',
    'profit', 'ltv90_revenue'] as const) {
    ;(s[k] as number | null) = addN(k)
  }
  if (s.gross_profit !== null) s.gross_profit = r2(s.gross_profit)
  if (s.profit !== null) s.profit = r2(s.profit)
  const qualityKnown = rows.some((r) => nz(r.quality_rate) !== null)
  s.ctr = s.impressions ? r2((s.link_clicks / s.impressions) * 100) : null
  s.cpc = s.link_clicks ? r2(s.spend / s.link_clicks) : null
  s.cpm = s.impressions ? r2((s.spend / s.impressions) * 1000) : null
  s.cost_per_chat = s.spend > 0 && s.chats ? r2(s.spend / s.chats) : null
  s.roas = s.spend > 0 ? r2(s.revenue / s.spend) : null
  s.cost_per_order = s.spend > 0 && s.orders ? r2(s.spend / s.orders) : null
  s.chat_to_order = s.chats ? r1((s.orders_chat / s.chats) * 100) : null
  s.aov = s.orders ? r2(s.revenue / s.orders) : null
  s.hook_rate = s.video_3s && s.impressions ? r1((s.video_3s / s.impressions) * 100) : null
  s.hold_rate = s.video_3s ? r1((s.thruplays / s.video_3s) * 100) : null
  s.margin_pct = s.gross_profit !== null && s.revenue > 0 ? r1((s.gross_profit / s.revenue) * 100) : null
  s.breakeven_roas = s.margin_pct ? r2(100 / s.margin_pct) : null
  s.cac = s.spend > 0 && s.new_orders ? r2(s.spend / s.new_orders) : null
  s.ltv90_complete = rows.length > 0 && rows.every((r) => r.ltv90_complete)
  s.quality_rate = qualityKnown && s.chats ? r1((s.chats_quality / s.chats) * 100) : null
  s.cost_per_quality_chat = qualityKnown && s.spend > 0 && s.chats_quality ? r2(s.spend / s.chats_quality) : null
  s.cost_per_buyer = qualityKnown && s.spend > 0 && s.buyers ? r2(s.spend / s.buyers) : null
  return s
}

// ---------------------------------------------------------------- saved views

export interface TableState {
  level: AdLevel
  columns: string[]
  sort: { key: string; dir: SortDir }
  filters: TableFilters
}

export const DEFAULT_STATE: TableState = { level: 'campaign', columns: [...DEFAULT_COLUMNS], sort: { key: 'spend', dir: 'desc' }, filters: {} }

/** A state from anything (an old saved view, a newer server's keys): unknown columns / sort / level fall back. */
export function cleanState(s: Partial<TableState> | null | undefined): TableState {
  const level: AdLevel = s?.level === 'adset' || s?.level === 'ad' ? s.level : 'campaign'
  const columns = cleanColumns(s?.columns)
  const key = s?.sort?.key && COL.has(s.sort.key) ? s.sort.key : 'spend'
  const dir: SortDir = s?.sort?.dir === 'asc' ? 'asc' : 'desc'
  const f = s?.filters ?? {}
  const filters: TableFilters = {}
  if (STATUS_OPTIONS.some((o) => o.v && o.v === f.status)) filters.status = f.status
  if (FLAG_OPTIONS.some((o) => o.v && o.v === f.flag)) filters.flag = f.flag
  const q = (f.q ?? '').trim()
  if (q) filters.q = q.slice(0, 80)
  return { level, columns, sort: { key, dir }, filters }
}

export const stateOfView = (v: SavedView): TableState => cleanState(v)

export function sameState(a: TableState, b: TableState): boolean {
  const k = (s: TableState) => JSON.stringify(cleanState(s))
  return k(a) === k(b)
}

export const MAX_VIEW_NAME = 40

/** Why a name cannot be used (Thai), or null. Names are compared trimmed; renaming a view to its own name is fine. */
export function nameProblem(name: string, views: readonly SavedView[], exceptId: string | null = null): string | null {
  const n = name.trim()
  if (!n) return 'ตั้งชื่อมุมมองก่อน'
  if (n.length > MAX_VIEW_NAME) return `ชื่อยาวได้ไม่เกิน ${MAX_VIEW_NAME} ตัวอักษร`
  if (views.some((v) => v.id !== exceptId && v.name.trim() === n)) return 'มีมุมมองชื่อนี้แล้ว'
  return null
}

export const newViewId = (now: number = Date.now(), r: number = Math.random()): string => `v-${now.toString(36)}${Math.floor(r * 1296).toString(36)}`

export const viewOf = (s: TableState, id: string, name: string): SavedView => ({ id, name: name.trim(), ...cleanState(s) })

/** Replace the view with the same id, or add it at the end. */
export function upsertView(views: readonly SavedView[], v: SavedView): SavedView[] {
  return views.some((x) => x.id === v.id) ? views.map((x) => (x.id === v.id ? v : x)) : [...views, v]
}
/** What the views menu asks the table to do (the table saves the whole list and answers with an error or null). */
export type ViewOp = { kind: 'new'; name: string } | { kind: 'save' } | { kind: 'rename'; id: string; name: string } | { kind: 'remove'; id: string }

export const renameView = (views: readonly SavedView[], id: string, name: string): SavedView[] => views.map((v) => (v.id === id ? { ...v, name: name.trim() } : v))
export const removeView = (views: readonly SavedView[], id: string): SavedView[] => views.filter((v) => v.id !== id)

// ---------------------------------------------------------------- ภาพรวม

export interface OverviewKpi {
  key: string
  label: string
  value: number | null
  prev: number | null
  unit: 'baht' | 'count' | 'ratio'
  /** which way is good — null = neither (spend: more is not good or bad by itself) */
  better: 'up' | 'down' | null
  /** the quiet line under the number */
  sub: string | null
}

export function overviewKpis(o: AdsOverview): OverviewKpi[] {
  const t = o.totals
  const p = o.prev
  // the POS answered, with the shop's daily numbers (an older POS without `store`: store sales unknown)
  const pos = o.web_source.ok && o.web_source.store !== false
  const days = Math.max(1, o.period.days)
  return [
    { key: 'spend', label: 'ค่าแอด', value: t.spend, prev: p?.spend ?? null, unit: 'baht', better: null, sub: `เฉลี่ย ${money(t.spend / days)} ต่อวัน` },
    {
      key: 'revenue', label: 'ยอดขายร้าน', value: pos ? t.revenue : null, prev: pos ? (p?.revenue ?? null) : null, unit: 'baht', better: 'up',
      sub: pos ? `${count(t.orders)} ออเดอร์ · ไม่รวม Shopee/TikTok` : null,
    },
    { key: 'mer', label: 'MER', value: t.mer, prev: p?.mer ?? null, unit: 'ratio', better: 'up', sub: 'ยอดขายร้าน ÷ ค่าแอด' },
    { key: 'new_customers', label: 'ลูกค้าใหม่', value: t.new_customers, prev: p?.new_customers ?? null, unit: 'count', better: 'up', sub: 'เบอร์ที่ไม่เคยสั่งมาก่อน' },
    { key: 'cac', label: 'CAC', value: t.cac, prev: p?.cac ?? null, unit: 'baht', better: 'down', sub: 'ค่าแอด ÷ ลูกค้าใหม่' },
    {
      key: 'profit_after_ads', label: 'กำไรหลังหักค่าแอด', value: t.profit_after_ads, prev: p?.profit_after_ads ?? null, unit: 'baht', better: 'up',
      sub: t.margin_pct !== null ? `มาร์จิ้น ${pct(t.margin_pct)} ก่อนหักค่าแอด` : null,
    },
  ]
}

/** "+12%" with good / bad / flat by the KPI's direction (null = nothing to compare). */
export function kpiDelta(k: Pick<OverviewKpi, 'value' | 'prev' | 'unit' | 'better'>): { text: string; tone: 'good' | 'bad' | 'flat'; dir: 'up' | 'down' | null } | null {
  const v = k.value
  const p = k.prev
  if (v === null || p === null) return null
  const diff = v - p
  const dir = diff > 0 ? 'up' : diff < 0 ? 'down' : null
  const tone = dir === null || k.better === null ? 'flat' : dir === k.better ? 'good' : 'bad'
  if (p === 0) return v === 0 ? { text: 'เท่าเดิม', tone: 'flat', dir: null } : { text: `+${k.unit === 'ratio' ? v.toFixed(2) : count(v)}`, tone, dir }
  // a loss that shrinks / grows: percent of a negative base reads backwards — say it in baht
  if (p < 0 || v < 0) {
    const d = Math.round(diff)
    return { text: d === 0 ? 'เท่าเดิม' : `${d > 0 ? '+' : '−'}${money(Math.abs(d))}`, tone: d === 0 ? 'flat' : tone, dir }
  }
  const r = Math.round((diff / p) * 100)
  return { text: r === 0 ? 'เท่าเดิม' : `${r > 0 ? '+' : ''}${r}%`, tone: r === 0 ? 'flat' : tone, dir }
}

/** Shopee + TikTok share of all sales (store + marketplace), for the marketplace line. */
export function marketShare(o: Pick<AdsOverview, 'totals' | 'marketplace'>): number | null {
  const all = o.totals.revenue + o.marketplace.revenue
  return all > 0 ? (o.marketplace.revenue / all) * 100 : null
}

// ---------------------------------------------------------------- สิ่งที่ควรทำ

export interface RecSettings {
  alert_spend?: number
  rec_scale_mult?: number
  rec_scale_min_orders?: number
  rec_lowq_min_chats?: number
  rec_lowq_max_pct?: number
}

export const REC_KINDS: { kind: RecKind; label: string; icon: IconName; tone: Tone; rule: (s: RecSettings) => string }[] = [
  { kind: 'burn', label: 'เผาเงิน', icon: 'flame', tone: 'crit', rule: (s) => `ใช้ถึง ${money(s.alert_spend ?? 500)} ใน 24 ชม. แต่ยังไม่มีแชทหรือออเดอร์ (Meta ก็นับได้ 0)` },
  { kind: 'high_cost', label: 'ต้นทุนสูง', icon: 'trendDown', tone: 'warn', rule: (s) => `ROAS 7 วันต่ำกว่าจุดคุ้มทุน และใช้ไปตั้งแต่ ${money(s.alert_spend ?? 500)}` },
  {
    kind: 'scale', label: 'ควรเพิ่มงบ', icon: 'trendUp', tone: 'good',
    rule: (s) => `ROAS 7 วันตั้งแต่ ${s.rec_scale_mult ?? 1.5} เท่าของจุดคุ้มทุน และมีออเดอร์ตั้งแต่ ${s.rec_scale_min_orders ?? 5} ออเดอร์`,
  },
  { kind: 'fatigue', label: 'แอดล้า', icon: 'repeat', tone: 'warn', rule: () => 'คนเห็นซ้ำบ่อยขึ้นแต่คลิกน้อยลง เทียบกับ 3 วันแรกของแอด' },
  {
    kind: 'low_quality', label: 'คนทักเยอะแต่ไม่ซื้อ', icon: 'chat', tone: 'warn',
    rule: (s) => `แชทตั้งแต่ ${s.rec_lowq_min_chats ?? 20} แชทใน 7 วัน แต่แชทคุณภาพต่ำกว่า ${s.rec_lowq_max_pct ?? 20}% หรือยังไม่มีคนซื้อ`,
  },
]

/** Every kind in a fixed order (empty ones too); inside: most urgent first, then the most money. */
export function groupRecs(items: readonly Recommendation[]): { kind: RecKind; items: Recommendation[] }[] {
  return REC_KINDS.map(({ kind }) => ({
    kind,
    items: items.filter((i) => i.kind === kind).sort((a, b) => b.severity - a.severity || (b.metrics.spend ?? 0) - (a.metrics.spend ?? 0)),
  }))
}

export const SEVERITY: Record<1 | 2 | 3, string> = { 3: 'ดูวันนี้', 2: 'ดูภายในสัปดาห์', 1: 'เมื่อมีเวลา' }

/** The key numbers of a card (only those the server sent). */
/** `partial` = the real Page is not in the chat system yet (quality_source 'test_only'): our sales / chats per ad are
 *  web-only then, so the card shows Meta's count (ROAS · purchases · conversations started) instead of ours */
export function recNumbers(
  m: Partial<AdMetrics> & { spend_24h?: number | null; meta_roas?: number | null },
  partial = false,
): { label: string; value: string; tone?: Tone }[] {
  const out: { label: string; value: string; tone?: Tone }[] = []
  const has = (k: keyof AdMetrics) => m[k] !== undefined
  const vsBe = (r: number | null | undefined) => (has('breakeven_roas') && nz(m.breakeven_roas) !== null ? `${roasText(r)} / คุ้มทุน ${roasText(m.breakeven_roas)}` : roasText(r))
  if (m.spend_24h !== undefined && m.spend_24h !== null) out.push({ label: 'ใช้ไปใน 24 ชม.', value: money(m.spend_24h) })
  if (has('spend')) out.push({ label: 'ใช้จ่าย', value: money(m.spend) })
  if (partial) {
    if (m.meta_roas !== undefined) out.push({ label: 'ROAS (Meta)', value: vsBe(m.meta_roas) })
    if (has('meta_purchases')) out.push({ label: 'Meta: ซื้อ', value: count(m.meta_purchases) })
    if (has('meta_chats')) out.push({ label: 'Meta: เริ่มแชท', value: count(m.meta_chats) })
  } else {
    if (has('revenue')) out.push({ label: 'ยอดขาย', value: money(m.revenue) })
    if (has('roas')) out.push({ label: 'ROAS', value: vsBe(m.roas) })
    if (has('profit')) out.push({ label: 'กำไร', value: moneySigned(m.profit), tone: profitTone(m.profit) ?? undefined })
    if (has('orders')) out.push({ label: 'ออเดอร์', value: count(m.orders) })
    if (has('chats')) out.push({ label: 'แชท', value: count(m.chats) })
    if (has('quality_rate')) out.push({ label: 'แชทคุณภาพ', value: pct(m.quality_rate) })
    if (has('buyers')) out.push({ label: 'คนซื้อ', value: count(m.buyers) })
  }
  if (has('frequency')) out.push({ label: 'ความถี่', value: freqText(m.frequency) })
  if (has('ctr')) out.push({ label: 'CTR', value: ctrText(m.ctr) })
  return out
}

// ---------------------------------------------------------------- ป้าย AI ครีเอทีฟ

const TAG_LABEL: Record<string, string> = {
  menu: 'เมนูอาหาร', food: 'เมนูอาหาร', review: 'รีวิวลูกค้า', reviews: 'รีวิวลูกค้า', before_after: 'ก่อน-หลัง', ugc: 'คนจริง UGC',
  promo: 'โปร-ราคา', promo_price: 'โปร-ราคา', price: 'โปร-ราคา', nutrition: 'ข้อมูลโภชนาการ', weight_loss: 'ลดน้ำหนัก', health: 'สุขภาพ', convenience: 'สะดวก',
  value: 'คุ้มราคา',
}
export const tagLabel = (t: string): string => TAG_LABEL[t] ?? t

/** The chips under a creative: content · selling points · มีคนในภาพ · มีข้อความบนภาพ (each once). */
export function tagChips(t: CreativeTags | null | undefined): { text: string; kind: 'content' | 'sell' | 'flag' }[] {
  if (!t) return []
  const seen = new Set<string>()
  const out: { text: string; kind: 'content' | 'sell' | 'flag' }[] = []
  const push = (text: string, kind: 'content' | 'sell' | 'flag') => {
    if (!seen.has(text)) {
      seen.add(text)
      out.push({ text, kind })
    }
  }
  for (const c of t.content ?? []) push(tagLabel(c), 'content')
  for (const c of t.selling_points ?? []) push(tagLabel(c), 'sell')
  if (t.has_person) push('มีคนในภาพ', 'flag')
  if (t.has_text) push('มีข้อความบนภาพ', 'flag')
  return out
}

/** "ใช้ไป ฿41.60 / เพดาน ฿300 · ติดป้ายแล้ว 12 ชิ้น" + the server's note (why it stopped / what it is doing) + the state. */
export function aiBudgetLine(b: CreativeAiBudget | null | undefined): { text: string; note: string | null; state: 'ok' | 'near' | 'stopped' } | null {
  if (!b) return null
  const parts = [`ใช้ไป ${baht2(b.spent)} / เพดาน ${money(b.cap)}`]
  if (b.tagged !== undefined) parts.push(`ติดป้ายแล้ว ${count(b.tagged)} ชิ้น`)
  if (b.failed) parts.push(`ติดไม่สำเร็จ ${count(b.failed)} ชิ้น`)
  let note = b.note ?? null
  if (!note && b.pending) note = `รอติดป้าย ${count(b.pending)} ชิ้น`
  if (!note && b.stopped) note = b.spent >= b.cap ? 'ใช้ครบเพดานแล้ว หยุดติดป้ายใหม่' : 'หยุดติดป้ายอยู่'
  const state = b.stopped ? 'stopped' : b.cap > 0 && b.spent >= b.cap * 0.8 ? 'near' : 'ok'
  return { text: parts.join(' · '), note, state }
}

/** The AI tag groups of the creatives report (shown in their own block). */
export const TAG_GROUPS = new Set(['content', 'selling_point', 'has_person', 'has_text'])

/** p as people read it: p = 0.012 · p < 0.001 */
export const pText = (p: number | null | undefined): string => (p === null || p === undefined ? '—' : p < 0.001 ? 'p < 0.001' : `p = ${p.toFixed(3)}`)
